import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { moneyFromString } from "@/shared/lib/money";

import type { RevenueCurrencyTotals } from "../lib/revenue-filter";

interface KpiCardProps {
  label: string;
  value: string;
  testId: string;
}

function KpiCard({ label, value, testId }: KpiCardProps): JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-[var(--sp-1)] rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-[var(--sp-4)] py-[var(--sp-3)] shadow-[var(--sh-1)]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
        {label}
      </span>
      <span data-testid={testId} className="num text-[20px] font-bold text-[var(--text-primary)]">
        {value}
      </span>
    </div>
  );
}

interface RevenueKpiStripProps {
  summary: RevenueCurrencyTotals[];
}

export function RevenueKpiStrip({ summary }: RevenueKpiStripProps): JSX.Element {
  const { t } = useTranslation("revenue_report");

  return (
    <div className="mb-[var(--sp-4)] flex flex-col gap-[var(--sp-3)]">
      {summary.map((bucket) => (
        <div key={bucket.currency} className="flex gap-[var(--sp-3)]">
          <KpiCard
            label={`${t("kpi.orders")} (${bucket.currency})`}
            value={String(bucket.orders)}
            testId={`revenue-kpi-orders-${bucket.currency.toLowerCase()}`}
          />
          <KpiCard
            label={`${t("kpi.gross")} (${bucket.currency})`}
            value={moneyFromString(bucket.gross, bucket.currency)}
            testId={`revenue-kpi-gross-${bucket.currency.toLowerCase()}`}
          />
          <KpiCard
            label={`${t("kpi.fees")} (${bucket.currency})`}
            value={moneyFromString(bucket.fees, bucket.currency)}
            testId={`revenue-kpi-fees-${bucket.currency.toLowerCase()}`}
          />
          <KpiCard
            label={`${t("kpi.net")} (${bucket.currency})`}
            value={moneyFromString(bucket.net, bucket.currency)}
            testId={`revenue-kpi-net-${bucket.currency.toLowerCase()}`}
          />
        </div>
      ))}
    </div>
  );
}
